import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Divider, Flex, Form, Space, Tag, Upload } from 'antd'
import { UploadFile } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceGetInquiryEndpoint,
  useStocksServiceGetIssuanceEndpoint
} from 'src/api/queries'
import {
  InventoryDocumentInquiryItemResponse,
  InventoryDocumentState,
  InventoryItemResponse
} from 'src/api/requests'
import { ArrowSwapHorizontal } from 'src/assets'
import {
  CustomItemRender,
  LoadingScreen,
  PageBreadcrumbs,
  Text
} from 'src/components'
import {
  CommentSection,
  ConsumableListCreate
} from 'src/features/stock/components'
import { useStyles } from 'src/features/stock/components/styles'
import { getStatus } from 'src/features/stock/utils/'
import { onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

dayjs.extend(utc)
dayjs.extend(timezone)

type Document = UploadFile & { id?: string }

type FormattedInquiryItem = {
  modifiedQuantity: number
  items: InventoryItemResponse[]
} & InventoryDocumentInquiryItemResponse

export const StockApplicationsDetails = () => {
  const [form] = Form.useForm()

  const { t, currentLang } = useLocales()
  const palette = useTheme()
  const { styles } = useStyles()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const onBackClick = () => {
    navigate(paths.stock.applications)
  }

  const { data, isLoading } = useStocksServiceGetInquiryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const isConfirmed =
    data?.state === InventoryDocumentState.CONFIRMED ||
    data?.state === InventoryDocumentState.ACCEPTED

  const isRejected = data?.state === InventoryDocumentState.REJECTED

  const isNew = data?.state === InventoryDocumentState.NEW

  const { data: issuance, isLoading: isIssuanceLoading } =
    useStocksServiceGetIssuanceEndpoint(
      {
        id: String(data?.relatedDocuments?.[0]?.id)
      },
      [data?.relatedDocuments?.[0]?.id],
      {
        enabled: !!data?.relatedDocuments?.[0]?.id && isConfirmed
      }
    )

  useEffect(() => {
    if (id && data) {
      let inquiryItems: FormattedInquiryItem[] = []

      const isModifiedAndAccepted =
        data.state === InventoryDocumentState.MODIFIED_AND_ACCEPTED
      if (isModifiedAndAccepted) {
        const mergedMap = new Map<string, FormattedInquiryItem>()

        for (const item of data.items || []) {
          const key = item?.internationalName?.id ?? ''

          const prefixes = item?.internationalName?.activeSubstances?.map(
            (item) => item?.prefix
          )

          const prefixLabel = (prefixes ?? [])
            .filter((p) => p?.trim() !== '')
            .join('+')

          const existing = mergedMap?.get(key) ?? {
            name: `${item?.internationalName?.name}${prefixLabel ? ` (${prefixLabel})` : ''} ${item?.internationalName?.dosage}`,
            internationalNameId: item?.internationalName?.id,
            form: {
              name: item?.productForm?.name,
              id: item?.productForm?.id
            },
            quantity: 0,
            modifiedQuantity: 0,
            items: []
          }

          if (item?.isArchived) {
            existing.quantity = item?.quantity
          } else {
            existing.modifiedQuantity = item?.quantity ?? 0
          }

          existing.items = [
            ...(existing?.items || []),
            ...(item?.beneficiaryStocks || [])
          ]
          mergedMap.set(key, existing)
        }

        inquiryItems = Array.from(mergedMap.values())
      } else {
        inquiryItems = (data.items || []).map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: item?.productForm,
          quantity: item?.quantity ?? 0,
          modifiedQuantity: 0,
          items: item.beneficiaryStocks || []
        }))
      }

      form.setFieldsValue({
        contractId: data?.supplier?.name,
        comment: data?.comment,
        documentDate: data?.documentDate,
        documentNumber: data?.documentNumber,
        inquiryItems
      })
    }
  }, [data, id, form])

  if (isLoading || isIssuanceLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock_applications.inquiry_number')}: #${data?.code}`}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: `${t('stock_applications.inquiry_number')}: #${data?.code}`
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            {data?.state === InventoryDocumentState.CONFIRMED && (
              <Button
                onClick={() =>
                  navigate(paths.stock.issuanceDetails(String(data?.id)))
                }
              >
                {t('stock_applications.see_application_details')}
              </Button>
            )}

            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>
          </Space>
        </Flex>

        <Form form={form}>
          <Flex gap={16} vertical ref={printableRef}>
            <Card>
              <Flex vertical gap={8}>
                <Flex justify="space-between">
                  <Space>
                    <Text weight={400}>
                      {t('stock_applications.inquiry_number')}:
                    </Text>
                    <Text strong>#{data?.code}</Text>
                  </Space>
                  <Tag
                    color={
                      getStatus(t, data?.state ?? InventoryDocumentState.NEW)
                        .color
                    }
                  >
                    {
                      getStatus(t, data?.state ?? InventoryDocumentState.NEW)
                        .text
                    }
                  </Tag>
                </Flex>
                <Space>
                  <Flex gap={8}>
                    <Text weight={400}>
                      {t('stock_applications.request_date')}:
                    </Text>
                    <Text strong>
                      {dayjs
                        .utc(data?.documentDate)
                        .format(dayMonthYearHourMinutesFormatSeparator)}
                    </Text>
                    <ArrowSwapHorizontal />

                    <Text weight={400}>
                      {t('stock_applications.entry_date')}:
                    </Text>
                    <Text strong>
                      {issuance?.takeoverDate
                        ? dayjs(issuance?.takeoverDate).format(
                            dayMonthYearHourMinutesFormatSeparator
                          )
                        : '-'}
                    </Text>
                  </Flex>
                </Space>

                {data?.processedOn && (
                  <Space>
                    <Text weight={400}>
                      {isRejected
                        ? `${t('stock_applications.refusal_date')}:`
                        : `${t('stock_applications.accepted_date')}:`}
                    </Text>
                    <Text strong>
                      {dayjs
                        .utc(data?.processedOn)
                        .tz(dayjs.tz.guess())
                        .format(dayMonthYearHourMinutesFormatSeparator)}
                    </Text>
                  </Space>
                )}

                {data?.updateReason && (
                  <Space>
                    <Text weight={400}>{t('stock.edit_reason')}:</Text>
                    <Text strong>
                      {data?.updateReason?.translation?.[currentLang.value]}
                    </Text>
                  </Space>
                )}

                <Space>
                  <Text weight={400}>
                    {t('stock_applications.initiated_by')}:
                  </Text>
                  <Text strong>
                    {data?.initializedBy?.firstName}{' '}
                    {data?.initializedBy?.lastName} -
                  </Text>
                  <Text strong>{data?.beneficiary?.name}</Text>
                </Space>

                {data?.processedBy && (
                  <Space>
                    <Text weight={400}>
                      {isRejected
                        ? `${t('stock_applications.refused_by')}:`
                        : `${t('stock_applications.accepted_by')}:`}
                    </Text>
                    <Text strong>
                      {data?.processedBy?.firstName}{' '}
                      {data?.processedBy?.lastName} -
                    </Text>
                    <Text strong>{data?.beneficiary?.name}</Text>
                  </Space>
                )}

                <Space>
                  <Text weight={400}>
                    {t('stock_applications.requested_to')}:
                  </Text>
                  <Text strong>{data?.supplier?.name}</Text>
                </Space>
              </Flex>
            </Card>

            <Flex vertical gap={16}>
              <Card>
                <Form.Item name="inquiryItems">
                  <ConsumableListCreate isFinished={!isNew} form={form} />
                </Form.Item>
              </Card>
              <Card>
                <Flex vertical gap={16}>
                  {data?.beneficiaryComment && isRejected && (
                    <Card
                      title={t('epidemiology.refuse_reason')}
                      className={styles.commentCardWrapper}
                    >
                      <Divider className={styles.commentDivider} />
                      <Flex vertical gap={8} className={styles.commentCard}>
                        <div className={styles.commentBeneficiary}>
                          {data?.beneficiaryComment}
                        </div>
                        <Text size="lg" color={palette.colorTextTertiary}>
                          {data?.beneficiaryCommentBy?.firstName}{' '}
                          {data?.beneficiaryCommentBy?.lastName} -{' '}
                          {data?.supplier?.name}
                        </Text>
                      </Flex>
                    </Card>
                  )}

                  <Card title={t('stock_applications.uploaded_documents')}>
                    <Upload
                      itemRender={(_, file: Document) => (
                        <CustomItemRender
                          file={file}
                          onDownload={() =>
                            onDownloadFile(t, file?.id, file?.name)
                          }
                        />
                      )}
                      defaultFileList={data?.mediaFiles?.map((file, index) => ({
                        ...file,
                        uid: String(file?.id),
                        name: file?.name ?? `file-${index}`
                      }))}
                      disabled
                    />
                  </Card>

                  {data?.comment && <CommentSection data={data} />}
                </Flex>
              </Card>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
